import { memo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Dropdown from '../../Dropdown';

const OutlineFilterClasses = (isFilterEmpty, radiusClassName = 'rounded-2xl border-2', filterSpacingClass = 'py-3 px-4', fontSize = 'font-medium', bgClass = 'bg-fields-0') => (`flex ${bgClass} justify-between
cursor-pointer focus:outline-none
${clsx({
    'border-theme bg-theme bg-opacity-10 border-opacity-40': !isFilterEmpty,
    'border-grey-500 border-opacity-20': isFilterEmpty,
  }, radiusClassName, filterSpacingClass, fontSize)}`);
const StandardFilterClasses = (isFilterEmpty, bgClass = 'bg-filter') => (`rounded-2xl flex ${bgClass} py-3 px-4
font-medium cursor-pointer focus:outline-none
${clsx({ 'bg-opacity-WhenFilterNotEmpty': !isFilterEmpty, 'bg-opacity-whenFilterEmpty': isFilterEmpty })}`);

const WithoutBgFilterClasses = () => 'flex px-2 cursor-pointer focus:outline-none text-sm md:text-lg';

const TextFilterClasses = (isFilterEmpty, filterSpacingClass = 'py-3 px-4') => (`rounded-2xl flex bg-white justify-between
text-theme font-medium cursor-pointer focus:outline-none
${clsx(filterSpacingClass, { 'bg-opacity-50': !isFilterEmpty, 'bg-opacity-25': isFilterEmpty })}`);

const TransparentOutlineFilterClasses = (isFilterEmpty) => (`rounded-2xl bg-transparent border-2
py-2 px-3 inline-block font-bold focus:outline-none
${clsx({ 'bg-opacity-50': !isFilterEmpty, 'bg-opacity-25': isFilterEmpty })}`);

const TransparentGrayFilterClasses = (isFilterEmpty, textClasses = 'bg-gray-600 text-gray-600', filterSpacingClass = 'py-3 px-3 min-w-40') => (`rounded-2xl
inline-block focus:outline-none flex justify-around ${textClasses} ${filterSpacingClass}
${clsx({ 'bg-opacity-5': !isFilterEmpty, 'bg-opacity-10': isFilterEmpty })}`);

const GrayFilterClasses = (isFilterEmpty, textClasses = 'bg-gray-600 text-gray-600', filterSpacingClass = 'py-3 px-3 min-w-40') => (`rounded-2xl
inline-block  bg-gray-500 border focus:outline-none flex justify-between ${textClasses} ${filterSpacingClass}
${clsx({ 'bg-opacity-10': !isFilterEmpty, 'bg-opacity-5': 'bg-opacity-10' })}`);

const getFilterClasses = (variant, isFilterEmpty, filterSpacingClass, fontSize, bgClass) => {
  switch (variant) {
    case 'outline':
      return OutlineFilterClasses(isFilterEmpty, undefined, filterSpacingClass, fontSize, bgClass);
    case 'outline-no-radius':
      return OutlineFilterClasses(isFilterEmpty, 'border', undefined, fontSize, bgClass);
    case 'outline-left-radius':
      return OutlineFilterClasses(isFilterEmpty, 'rounded-l-2xl border', filterSpacingClass, fontSize, bgClass);
    case 'outline-right-radius':
      return OutlineFilterClasses(isFilterEmpty, 'rounded-r-2xl border', filterSpacingClass, fontSize, bgClass);
    case 'text':
      return TextFilterClasses(isFilterEmpty, filterSpacingClass);
    case 'transparentOutline':
      return TransparentOutlineFilterClasses(isFilterEmpty);
    case 'transparentGrayFilterClasses':
      return TransparentGrayFilterClasses(isFilterEmpty);
    case 'transparentWhiteFilterClasses':
      return TransparentGrayFilterClasses(isFilterEmpty, 'text-white', filterSpacingClass);
    case 'GrayFilterClasses':
      return GrayFilterClasses(isFilterEmpty, 'text-black', filterSpacingClass);
    case 'WithoutBgFilterClasses':
      return WithoutBgFilterClasses();
    case 'white':
      return variant;
    default: return StandardFilterClasses(isFilterEmpty, bgClass);
  }
};

function Filter({
  children,
  label,
  tooltipText,
  tooltipPrependText,
  isFilterEmpty,
  handleClear,
  filterMaxWidthClass,
  menuWidthClass,
  hasCounter,
  itemCount,
  onClose,
  variant,
  withClear,
  dropdownAligning,
  widthInside,
  isSelectionOptionBlack,
  filterSpacingClass,
  fontSize,
  icon,
  doOpenDatePicker, setDoOpenDatePicker,
  bgClassName,
  textColorClassName,
  disabled,
  showChevronArrows,
}) {
  const renderToggleContent = () => {
    if (!isFilterEmpty && withClear) {
      return (
        <Dropdown.Toggle.Clear
          handleClear={handleClear}
          className="inline-block w-5"
        />
      );
    } if (showChevronArrows) {
      return (
        <Dropdown.Toggle.Chevron className={clsx('h-3 opacity-75', textColorClassName)} />
      );
    }
    return <></>;
  };

  return (
    <Dropdown
      className={filterMaxWidthClass}
      doOpenDatePicker={doOpenDatePicker}
      setDoOpenDatePicker={setDoOpenDatePicker}
      disabled={disabled}
    >
      <Dropdown.Toggle
        className={`flex ${getFilterClasses(variant, isFilterEmpty, filterSpacingClass, fontSize, bgClassName)}`}
        title={tooltipText || (typeof (label) === 'string' ? (tooltipPrependText && tooltipPrependText !== label ? `${tooltipPrependText}: ` : '') + label : undefined)}
      >
        <>
          {icon}
          {isSelectionOptionBlack
            ? (
              <span
                className={clsx('truncate', widthInside, textColorClassName)}
                dangerouslySetInnerHTML={{ __html: label }}
              />
            )
            : (
              <span className={clsx('truncate', widthInside, textColorClassName)}>
                {label}
              </span>
            )}
          {hasCounter && (
            <span className="max-w-15 mr-2">
              {itemCount ? ` +${itemCount}` : ''}
            </span>
          )}
        </>
        {renderToggleContent()}
      </Dropdown.Toggle>
      <Dropdown.Menu
        onClose={onClose}
        widthClass={menuWidthClass}
        align={dropdownAligning}
      >
        {children}
      </Dropdown.Menu>
    </Dropdown>
  );
}

Filter.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  handleClear: PropTypes.func,
  filterMaxWidthClass: PropTypes.string,
  filterSpacingClass: PropTypes.string,
  isFilterEmpty: PropTypes.bool.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  tooltipPrependText: PropTypes.string,
  tooltipText: PropTypes.string,
  menuWidthClass: PropTypes.string.isRequired,
  hasCounter: PropTypes.bool,
  itemCount: PropTypes.number,
  onClose: PropTypes.func,
  variant: PropTypes.string,
  withClear: PropTypes.bool,
  dropdownAligning: PropTypes.string,
  widthInside: PropTypes.string,
  isSelectionOptionBlack: PropTypes.bool,
  fontSize: PropTypes.string,
  icon: PropTypes.node,
  doOpenDatePicker: PropTypes.bool,
  setDoOpenDatePicker: PropTypes.func,
  bgClassName: PropTypes.string,
  textColorClassName: PropTypes.string,
  disabled: PropTypes.bool,
  showChevronArrows: PropTypes.bool,
};

Filter.defaultProps = {
  filterMaxWidthClass: 'max-w-filter',
  filterSpacingClass: undefined,
  hasCounter: false,
  itemCount: null,
  onClose: () => { },
  variant: 'standard',
  handleClear: () => { },
  withClear: true,
  dropdownAligning: 'left',
  widthInside: '',
  isSelectionOptionBlack: undefined,
  tooltipPrependText: undefined,
  tooltipText: undefined,
  fontSize: undefined,
  icon: undefined,
  doOpenDatePicker: false,
  setDoOpenDatePicker: () => { },
  bgClassName: undefined,
  textColorClassName: undefined,
  disabled: false,
  showChevronArrows: true,
};

export default memo(Filter);
